import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';
import {
  getAccountSetting,
  upsertAccountSetting as upsertAccountSettingApi,
} from '@kirin/api-client/api/settings';
import {
  SettingType,
  type SettingResponse,
  type SettingTypeValue,
} from '@kirin/api-client/contracts/settings';
import type { SeverityType } from '@kirin/api-client/contracts/shared/severity';
import type { SecretConfidence } from '@kirin/api-client/helpers/secret-confidence';

/**
 * Hook to fetch an account (user) setting by type.
 * Backend returns virtual defaults for defaultable types (e.g. enforcement threshold).
 */
export function useAccountSetting(
  settingType: SettingTypeValue,
  enabled: boolean
) {
  return useQuery({
    queryKey: queryKeys.accountSettings.detail(settingType),
    queryFn: () => getAccountSetting(apiClient, settingType),
    enabled: enabled && !!settingType,
    retry: retryHandlers.detail,
  });
}

/**
 * Hook to create or update an account setting by type.
 * Config is the type-specific payload (e.g. { severity: 'high' } for enforcement-severity-threshold).
 */
export function useUpsertAccountSetting(settingType: SettingTypeValue) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: Record<string, unknown>) => {
      return upsertAccountSettingApi(apiClient, settingType, config);
    },
    onSuccess: (data: SettingResponse) => {
      queryClient.setQueryData<SettingResponse>(
        queryKeys.accountSettings.detail(settingType),
        data
      );
    },
  });
}

/**
 * Hook for enforcement severity threshold (account scope).
 * Only enabled when enforce_policy_mode feature flag is on.
 * Backend returns a virtual default ({ severity: 'high' }) when unconfigured.
 */
export function useAccountEnforcementSeverityThreshold(enabled: boolean) {
  const query = useAccountSetting(
    SettingType.EnforcementSeverityThreshold,
    enabled
  );
  const config = query.data?.config as { severity?: string } | undefined;
  const severity: SeverityType | undefined = query.isError
    ? undefined
    : ((config?.severity as SeverityType | undefined) ?? 'high');
  return {
    ...query,
    severity,
  };
}

/**
 * Hook to update account enforcement severity threshold.
 */
export function useUpsertAccountEnforcementSeverityThreshold() {
  return useUpsertAccountSetting(SettingType.EnforcementSeverityThreshold);
}

/**
 * Hook for the Secret Alert Confidence Threshold (account scope, KIR-3765).
 * Only enabled when the secret_confidence_threshold feature flag is on.
 * Backend returns a virtual default ({ confidence: 'high' }) when unconfigured,
 * so we don't hardcode a different local default.
 */
export function useAccountSecretConfidenceThreshold(enabled: boolean) {
  const query = useAccountSetting(
    SettingType.SecretConfidenceThreshold,
    enabled
  );
  const config = query.data?.config as { confidence?: string } | undefined;
  const confidence: SecretConfidence | undefined = query.isError
    ? undefined
    : ((config?.confidence as SecretConfidence | undefined) ?? 'high');
  return {
    ...query,
    confidence,
  };
}

/**
 * Hook to update the account Secret Alert Confidence Threshold.
 */
export function useUpsertAccountSecretConfidenceThreshold() {
  return useUpsertAccountSetting(SettingType.SecretConfidenceThreshold);
}
